import { CURRENCY_LIST_OPTIONS, Select, TextField, convertToOptions, withStyles } from 'vopay';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '#shared/components/UI';
import styles from './SubAccountSearchForm.module.scss';

const cn = withStyles(styles);

export type SubAccountStatus = 'Active' | 'NotActive' | '';

type SubAccountSearchParams = {
  status: SubAccountStatus;
  searchTerm: string;
  currency: string;
  country: string;
};

type SubAccountSearchFormProps = {
  status: SubAccountStatus;
  currency: string;
  country: string;
  searchTerm: string;
  search: (searchParams: SubAccountSearchParams) => void;
  /** Prefixes the field names, so a second form on the page (the drawer's) doesn't share the list's. */
  fieldNamePrefix?: string;
};

// Ported from client-dashboard's SubAccountSearchForm (ID-13193). The fields are controlled local state
// seeded from props and re-synced when the props change (the URL query is the source of truth, held by the
// SubAccountList root), so the effects below reinitialize rather than compute derived state.
export default function SubAccountSearchForm({
  status,
  currency,
  country,
  searchTerm,
  search,
  fieldNamePrefix = '',
}: SubAccountSearchFormProps) {
  const [currentStatus, setCurrentStatus] = useState<SubAccountStatus>(status);
  const [currentCurrency, setCurrentCurrency] = useState<string>(currency);
  const [currentCountry, setCurrentCountry] = useState<string>(country);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList.SearchForm' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const { t: tCountries } = useTranslation('translations', { keyPrefix: 'Shared.Countries' });

  useEffect(() => {
    setCurrentSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  useEffect(() => {
    setCurrentCurrency(currency);
  }, [currency]);

  useEffect(() => {
    setCurrentCountry(country);
  }, [country]);

  const statusOptions: Record<Exclude<SubAccountStatus, ''>, string> = {
    Active: t('Status.Options.active', { defaultValue: 'Active' }),
    NotActive: t('Status.Options.notActive', { defaultValue: 'Not Active' }),
  };

  const currencyOptions = {
    ...CURRENCY_LIST_OPTIONS,
    EUR: 'EUR',
    GBP: 'GBP',
  };

  // Keyed by the raw country name the API returns (the filter matches it against each row's Country);
  // only the label is localized.
  const countryOptions = {
    Canada: tCountries('CA', { defaultValue: 'Canada' }),
    'United States': tCountries('USLong', { defaultValue: 'United States' }),
  };

  return (
    <div className={cn('searchArea')}>
      <Select
        version="2"
        placeholder={t('Country.placeholder', { defaultValue: 'Country' })}
        options={convertToOptions(countryOptions)}
        name={`${fieldNamePrefix}country`}
        value={currentCountry}
        onBlur={() => {}}
        showClear
        onChange={(e) => {
          setCurrentCountry(e.target.value);
        }}
        sort={false}
      />

      <Select
        version="2"
        placeholder={t('Currency.placeholder', { defaultValue: 'Currency' })}
        options={convertToOptions(currencyOptions)}
        name={`${fieldNamePrefix}currency`}
        value={currentCurrency}
        showClear
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentCurrency(e.target.value);
        }}
        sort={false}
      />

      <Select
        version="2"
        placeholder={t('Status.placeholder', { defaultValue: 'Status' })}
        options={convertToOptions(statusOptions)}
        name={`${fieldNamePrefix}status`}
        sort={false}
        showClear
        value={currentStatus}
        onBlur={() => {}}
        onChange={(e) => {
          const value = e.target.value;
          setCurrentStatus(value === 'Active' || value === 'NotActive' ? value : '');
        }}
      />

      <TextField
        key="AccountSearchKeyword"
        name={`${fieldNamePrefix}AccountSearchKeyword`}
        placeholder={t('keywordPlaceholder', { defaultValue: 'Keyword' })}
        value={currentSearchTerm}
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentSearchTerm(e.target.value);
        }}
        variant="outlineShadow"
        width="100%"
      />

      <Button
        id="search-elinx"
        colorScheme="primary"
        size="small"
        onClick={() =>
          search({
            status: currentStatus,
            currency: currentCurrency,
            country: currentCountry,
            searchTerm: currentSearchTerm,
          })
        }
      >
        {tButtons('search', { defaultValue: 'Search' })}
      </Button>
    </div>
  );
}
